import React from 'react';
import { Box, Container, Typography, Button, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowForward } from '@mui/icons-material';
import { fadeInUp, fadeInRight, staggerContainer, viewportConfig } from '../../utils/animations';
import { STATS, PEGASUS_URLS } from '../../utils/constants';
import useScrollTo from '../../hooks/useScrollTo';
import modernApartment from '../../assets/mordern_apartment_1.jpg';

const Hero = () => {
  const { scrollToSection } = useScrollTo();

  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      id="home"
      component="section"
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 50%, #1E3A5F 100%)',
        pt: { xs: 8, md: 4 },
        pb: { xs: 6, md: 4 },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <Typography
                component={motion.p}
                variants={fadeInUp}
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  mb: 2,
                  fontSize: '0.85rem',
                  letterSpacing: '0.15em',
                }}
              >
                Welcome to ITorama
              </Typography>

              <Typography
                component={motion.h1}
                variants={fadeInUp}
                variant="h1"
                sx={{
                  color: 'white',
                  mb: 3,
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  lineHeight: 1.1,
                }}
              >
                Your Trusted Partner in{' '}
                <Box
                  component="span"
                  sx={{
                    color: 'secondary.main',
                    display: 'inline',
                  }}
                >
                  Property Management
                </Box>
              </Typography>

              <Typography
                component={motion.p}
                variants={fadeInUp}
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  maxWidth: 520,
                  lineHeight: 1.8,
                }}
              >
                We are dedicated to maximizing your investment while minimizing the hassles
                that come with property ownership. Let us handle the details so you can
                focus on what matters most to you.
              </Typography>

              <Box
                component={motion.div}
                variants={fadeInUp}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: { xs: 4, md: 0 },
                }}
              >
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => handleExternalLink(PEGASUS_URLS.signup)}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  Get Started
                </Button>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection('contact')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial="hidden"
              animate="visible"
              variants={fadeInRight}
              sx={{
                position: 'relative',
                display: { xs: 'block', md: 'block' },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    backgroundColor: 'secondary.main',
                    borderRadius: '50%',
                    opacity: 0.3,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={modernApartment}
                  alt="Modern apartment building managed by ITorama"
                  sx={{
                    width: '100%',
                    height: { xs: 280, sm: 350, md: 450 },
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>

              <Paper
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                elevation={4}
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  left: { xs: 16, md: -30 },
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <Typography variant="h4" color="primary.main" fontWeight={700}>
                  500+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Properties Managed
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          sx={{
            mt: { xs: 6, md: 8 },
          }}
        >
          <Grid container spacing={2}>
            {STATS.map((stat, index) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                  sx={{
                    textAlign: 'center',
                    p: { xs: 2, md: 3 },
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'secondary.main',
                      fontWeight: 700,
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
